import api from "../axios_Interceptor/api";
import { SERVER_ADDRESS } from "../server";

export const DIRECT_UPLOAD_SESSIONS_URL = `${SERVER_ADDRESS}file/direct-upload/sessions`;
export const PRODUCT_CREATE_DIRECT_URL = `${SERVER_ADDRESS}product/product-create-direct`;
export const PRODUCT_IMPORTS_URL = `${SERVER_ADDRESS}product/imports`;

export type DirectUploadPurpose = "product-image" | "product-import" | "warehouse-image" | "warehouse-import";

export interface DirectUploadProgress {
  completedFiles: number;
  totalFiles: number;
  loadedBytes: number;
  totalBytes: number;
  percent: number;
}

export interface DirectUploadObject {
  objectId: string;
  checksumSha256: string;
}

export interface DirectUploadResult {
  sessionId: string;
  objects: DirectUploadObject[];
}

interface PresignedUpload {
  objectId: string;
  url: string;
  method?: "POST" | "PUT";
  fields?: Record<string, string>;
  headers?: Record<string, string>;
}

interface DirectUploadOptions {
  purpose: DirectUploadPurpose;
  liveSeq: number;
  concurrency?: number;
  retries?: number;
  signal?: AbortSignal;
  onProgress?: (progress: DirectUploadProgress) => void;
}

const abortError = () => new DOMException("업로드가 취소되었습니다.", "AbortError");
const IMAGE_MAX_BYTES = 10 * 1024 * 1024;
const XLSX_MAX_BYTES = 20 * 1024 * 1024;
const SESSION_MAX_BYTES = 1024 * 1024 * 1024;
const IMPORT_MAX_IMAGES = 500;

const fileKind = (file: File) => {
  const extension = file.name.split(".").pop()?.toLowerCase();
  return {
    isImage: extension === "jpg" || extension === "jpeg" || extension === "png",
    isXlsx: extension === "xlsx",
  };
};

const validateFiles = (files: File[], purpose: DirectUploadPurpose) => {
  const names = files.map(file => file.name.trim());
  if (new Set(names).size !== names.length) {
    throw new Error("같은 파일명을 한 번에 중복 등록할 수 없습니다.");
  }

  let imageCount = 0;
  let xlsxCount = 0;
  let totalBytes = 0;
  files.forEach(file => {
    const { isImage, isXlsx } = fileKind(file);
    if (!isImage && !isXlsx) throw new Error(`지원되지 않는 파일 형식입니다: ${file.name}`);
    if (file.size < 1) throw new Error(`빈 파일은 업로드할 수 없습니다: ${file.name}`);
    if (isImage && file.size > IMAGE_MAX_BYTES) throw new Error("이미지는 파일당 10MB 이하만 업로드할 수 있습니다.");
    if (isXlsx && file.size > XLSX_MAX_BYTES) throw new Error("엑셀 파일은 20MB 이하만 업로드할 수 있습니다.");
    if (isImage) imageCount += 1;
    if (isXlsx) xlsxCount += 1;
    totalBytes += file.size;
  });

  if (totalBytes > SESSION_MAX_BYTES) {
    throw new Error("한 번에 업로드하는 파일의 총 크기는 1GB를 초과할 수 없습니다.");
  }
  if (["product-image", "warehouse-image"].includes(purpose) && (xlsxCount || imageCount < 1 || imageCount > 10)) {
    throw new Error("상품 이미지는 한 번에 1~10장만 업로드할 수 있습니다.");
  }
  if (["product-import", "warehouse-import"].includes(purpose) && (xlsxCount !== 1 || imageCount > IMPORT_MAX_IMAGES)) {
    throw new Error("대량 등록은 xlsx 파일 1개와 이미지 최대 500개까지 업로드할 수 있습니다.");
  }
};

const wait = (milliseconds: number, signal?: AbortSignal) => new Promise<void>((resolve, reject) => {
  if (signal?.aborted) return reject(abortError());
  const done = () => {
    signal?.removeEventListener("abort", abort);
    resolve();
  };
  const timer = window.setTimeout(done, milliseconds);
  const abort = () => {
    window.clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
    reject(abortError());
  };
  signal?.addEventListener("abort", abort, { once: true });
});

export const uploadContentType = (file: File): string => {
  const extension = file.name.split(".").pop()?.toLowerCase();
  if (extension === "jpg" || extension === "jpeg") return "image/jpeg";
  if (extension === "png") return "image/png";
  if (extension === "xlsx") return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (extension === "xls") return "application/vnd.ms-excel";
  return file.type || "application/octet-stream";
};

export const sha256Base64 = async (file: Blob): Promise<string> => {
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", await file.arrayBuffer()));
  let binary = "";
  digest.forEach(byte => { binary += String.fromCharCode(byte); });
  return btoa(binary);
};

const uploadOnce = (
  upload: PresignedUpload,
  file: File,
  contentType: string,
  checksumSha256: string,
  signal: AbortSignal | undefined,
  onProgress: (loaded: number) => void,
) => new Promise<void>((resolve, reject) => {
  if (signal?.aborted) return reject(abortError());
  const request = new XMLHttpRequest();
  const abort = () => request.abort();
  const cleanup = () => signal?.removeEventListener("abort", abort);
  signal?.addEventListener("abort", abort, { once: true });
  request.open(upload.method ?? (upload.fields ? "POST" : "PUT"), upload.url);
  request.timeout = 120_000;
  Object.entries(upload.headers ?? {}).forEach(([name, value]) => request.setRequestHeader(name, value));
  if (!upload.fields && !Object.keys(upload.headers ?? {}).some(name => name.toLowerCase() === "content-type")) {
    request.setRequestHeader("Content-Type", contentType);
  }
  if (!upload.fields && !Object.keys(upload.headers ?? {}).some(name => name.toLowerCase() === "x-amz-checksum-sha256")) {
    request.setRequestHeader("x-amz-checksum-sha256", checksumSha256);
  }
  request.upload.onprogress = event => onProgress(event.lengthComputable ? Math.min(event.loaded, file.size) : 0);
  request.onerror = () => {
    cleanup();
    reject(new Error("S3 업로드에 실패했습니다."));
  };
  request.ontimeout = () => {
    cleanup();
    reject(new Error("S3 업로드 시간이 초과되었습니다."));
  };
  request.onabort = () => {
    cleanup();
    reject(abortError());
  };
  request.onload = () => {
    cleanup();
    if (request.status >= 200 && request.status < 300) {
      onProgress(file.size);
      resolve();
    } else {
      reject(Object.assign(new Error(`S3 업로드에 실패했습니다. (${request.status})`), { status: request.status }));
    }
  };
  if (upload.fields) {
    const form = new FormData();
    Object.entries(upload.fields).forEach(([name, value]) => form.append(name, value));
    form.append("file", file);
    request.send(form);
  } else {
    request.send(file);
  }
});

export const directUploadFiles = async (files: File[], options: DirectUploadOptions): Promise<DirectUploadResult> => {
  if (!Number.isInteger(options.liveSeq) || (options.purpose.startsWith("warehouse-") ? options.liveSeq !== 0 : options.liveSeq < 1)) throw new Error("방송 정보를 확인해주세요.");
  if (options.signal?.aborted) throw abortError();
  validateFiles(files, options.purpose);

  const concurrency = Math.max(1, Math.min(4, options.concurrency ?? 4));
  const checksums = new Array<string>(files.length);
  const contentTypes = files.map(uploadContentType);
  let checksumIndex = 0;
  await Promise.all(Array.from({ length: Math.min(concurrency, files.length) }, async () => {
    while (checksumIndex < files.length) {
      const index = checksumIndex++;
      if (options.signal?.aborted) throw abortError();
      checksums[index] = await sha256Base64(files[index]);
    }
  }));

  const session = await api.post(DIRECT_UPLOAD_SESSIONS_URL, {
    purpose: options.purpose,
    liveSeq: options.liveSeq,
    files: files.map((file, index) => ({
      name: file.name,
      size: file.size,
      contentType: contentTypes[index],
      checksumSha256: checksums[index],
    })),
  }, { headers: { "Content-Type": "application/json" }, signal: options.signal });
  const sessionId = session.data?.sessionId;
  const uploads = session.data?.uploads as PresignedUpload[] | undefined;
  if (typeof sessionId !== "string" || !Array.isArray(uploads) || uploads.length !== files.length) {
    throw new Error("업로드 주소를 확인할 수 없습니다.");
  }
  const objectIds = uploads.map(upload => upload.objectId);
  if (uploads.some(upload => !upload.url || !upload.objectId) || new Set(objectIds).size !== objectIds.length) {
    throw new Error("업로드 객체 정보를 확인할 수 없습니다.");
  }

  const loaded = new Array(files.length).fill(0);
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  let completedFiles = 0;
  const report = () => {
    const loadedBytes = loaded.reduce((sum, bytes) => sum + bytes, 0);
    options.onProgress?.({
      completedFiles,
      totalFiles: files.length,
      loadedBytes,
      totalBytes,
      percent: totalBytes ? Math.round(loadedBytes * 100 / totalBytes) : 100,
    });
  };
  report();

  let uploadIndex = 0;
  await Promise.all(Array.from({ length: Math.min(concurrency, files.length) }, async () => {
    while (uploadIndex < files.length) {
      const index = uploadIndex++;
      let attempt = 0;
      while (true) {
        try {
          await uploadOnce(uploads[index], files[index], contentTypes[index], checksums[index], options.signal, bytes => {
            loaded[index] = bytes;
            report();
          });
          completedFiles += 1;
          report();
          break;
        } catch (error) {
          loaded[index] = 0;
          report();
          const status = (error as { status?: number })?.status;
          if (options.signal?.aborted || attempt >= (options.retries ?? 2) || (status !== undefined && status < 500)) throw error;
          await wait(250 * 2 ** attempt++, options.signal);
        }
      }
    }
  }));

  const objects = uploads.map((upload, index) => ({ objectId: upload.objectId, checksumSha256: checksums[index] }));
  for (let attempt = 0; ; attempt += 1) {
    try {
      const completed = await api.post(`${DIRECT_UPLOAD_SESSIONS_URL}/${encodeURIComponent(sessionId)}/complete`, {
        objects,
      }, { headers: { "Content-Type": "application/json" }, signal: options.signal });
      if (completed.data?.state !== "uploaded" || completed.data?.sessionId !== sessionId) {
        throw Object.assign(new Error("업로드 완료 상태를 확인할 수 없습니다."), { status: 422 });
      }
      break;
    } catch (error) {
      const status = (error as { status?: number; response?: { status?: number } })?.response?.status
        ?? (error as { status?: number })?.status;
      if (options.signal?.aborted || attempt >= 1 || (status !== undefined && status < 500)) throw error;
      await wait(250, options.signal);
    }
  }
  return { sessionId, objects };
};
